import { API_ROUTES } from '@dgm/shared';

/**
 * Thin fetch client for the HTTP API. Auth is cookie-based (HttpOnly), so we
 * always send `credentials: 'include'` and never touch tokens in JS
 * (decisions-log #15). On a 401 we attempt a single silent refresh, then retry.
 */
const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  isRetry = false,
): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method,
    credentials: 'include',
    // X-Requested-With forces a CORS preflight on every call — our CSRF guard
    // for the SameSite=None auth cookies (a cross-site attacker's origin fails
    // the preflight against the API's strict origin allowlist).
    headers: {
      'x-requested-with': 'fetch',
      ...(body ? { 'content-type': 'application/json' } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  if (res.status === 401 && !isRetry && path !== API_ROUTES.refresh.path) {
    const refreshed = await tryRefresh();
    if (refreshed) return request<T>(method, path, body, true);
  }

  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) {
    throw new ApiError(res.status, data.error ?? `Request failed (${res.status})`, data.details);
  }
  return data as T;
}

async function tryRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${API_URL}${API_ROUTES.refresh.path}`, {
      method: 'POST',
      credentials: 'include',
    });
    return res.ok;
  } catch {
    return false;
  }
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};

/**
 * Games API client (Cycle-1 SG-1). Thin wrappers over API_ROUTES so paths
 * never drift from the backend. Gated server-side by FEATURES.gameCreation
 * (returns 404 when off); the dashboard also hides itself when the flag is off.
 */
const gamePath = (id: string) => API_ROUTES.getGame.path.replace('{gameId}', encodeURIComponent(id));

export const gamesApi = {
  list: <T>() => request<T>('GET', API_ROUTES.listGames.path),
  create: <T>(input: unknown) => request<T>('POST', API_ROUTES.createGame.path, input),
  get: <T>(id: string) => request<T>('GET', gamePath(id)),
  update: <T>(id: string, input: unknown) => request<T>('PATCH', gamePath(id), input),
  archive: <T>(id: string) =>
    request<T>('POST', API_ROUTES.archiveGame.path.replace('{gameId}', encodeURIComponent(id))),
  taxonomy: <T>() => request<T>('GET', API_ROUTES.templateTaxonomy.path),
  listJobs: <T>(id: string) =>
    request<T>('GET', API_ROUTES.listJobs.path.replace('{gameId}', encodeURIComponent(id))),
  enqueueJob: <T>(id: string, input: { kind: string; prompt: string }) =>
    request<T>(
      'POST',
      API_ROUTES.createJob.path.replace('{gameId}', encodeURIComponent(id)),
      input,
    ),
  sharedWithMe: <T>() => request<T>('GET', API_ROUTES.sharedWithMe.path),
  publish: <T>(id: string) =>
    request<T>('POST', API_ROUTES.publishGame.path.replace('{gameId}', encodeURIComponent(id))),
  unpublish: <T>(id: string) =>
    request<T>('POST', API_ROUTES.unpublishGame.path.replace('{gameId}', encodeURIComponent(id))),
  share: <T>(id: string, email: string) =>
    request<T>('POST', API_ROUTES.shareGame.path.replace('{gameId}', encodeURIComponent(id)), { email }),
  revokeShare: <T>(id: string, shareId: string) =>
    request<T>(
      'POST',
      API_ROUTES.revokeShare.path
        .replace('{gameId}', encodeURIComponent(id))
        .replace('{shareId}', encodeURIComponent(shareId)),
    ),
  playUrl: <T>(id: string, versionId: string) =>
    request<T>(
      'GET',
      API_ROUTES.playVersion.path
        .replace('{gameId}', encodeURIComponent(id))
        .replace('{versionId}', encodeURIComponent(versionId)),
    ),
  // version actions (VT-001..VT-004; server-gated by FEATURES.versionActions)
  restoreVersion: <T>(id: string, versionId: string) =>
    request<T>('POST', versionActionPath(API_ROUTES.restoreVersion.path, id, versionId)),
  branchVersion: <T>(id: string, versionId: string, input: { prompt: string; branchLabel?: string }) =>
    request<T>('POST', versionActionPath(API_ROUTES.branchVersion.path, id, versionId), input),
  archiveVersion: <T>(id: string, versionId: string) =>
    request<T>('POST', versionActionPath(API_ROUTES.archiveVersion.path, id, versionId)),
  unarchiveVersion: <T>(id: string, versionId: string) =>
    request<T>('POST', versionActionPath(API_ROUTES.unarchiveVersion.path, id, versionId)),
};

const versionActionPath = (template: string, id: string, versionId: string) =>
  template
    .replace('{gameId}', encodeURIComponent(id))
    .replace('{versionId}', encodeURIComponent(versionId));

/**
 * Friends/connections client (Cycle-1 SG-3). Server-gated by FEATURES.friends.
 * Requests are by exact email only (no search) — parents-only model (D-036).
 */
/** Public play resolution (D-137/D-138) — no auth required. */
export const playApi = {
  resolve: <T>(slug: string) =>
    request<T>('GET', API_ROUTES.resolvePlay.path.replace('{slug}', encodeURIComponent(slug))),
  url: <T>(slug: string) =>
    request<T>('GET', API_ROUTES.resolvePlayUrl.path.replace('{slug}', encodeURIComponent(slug))),
};

export const friendsApi = {
  list: <T>() => request<T>('GET', API_ROUTES.listConnections.path),
  sendRequest: <T>(email: string) =>
    request<T>('POST', API_ROUTES.sendFriendRequest.path, { email }),
  accept: <T>(otherSub: string) =>
    request<T>('POST', API_ROUTES.acceptFriendRequest.path.replace('{otherSub}', encodeURIComponent(otherSub))),
  decline: <T>(otherSub: string) =>
    request<T>('POST', API_ROUTES.declineFriendRequest.path.replace('{otherSub}', encodeURIComponent(otherSub))),
  remove: <T>(otherSub: string) =>
    request<T>('POST', API_ROUTES.removeFriend.path.replace('{otherSub}', encodeURIComponent(otherSub))),
  block: <T>(otherSub: string) =>
    request<T>('POST', API_ROUTES.blockFriend.path.replace('{otherSub}', encodeURIComponent(otherSub))),
};

export { API_URL };
